import { omit } from 'lodash-es';
import type { CoreTypes } from '../../core';
import type { Duty } from '../types/duties';
import toPerson from './persons';

const dutyRoleMap: Record<string, string> = {
  'Rektor': 'HEADTEACHER',
  'Lärare': 'TEACHER',
  'Förskollärare': 'PRESCHOOL_TEACHER',
  'Barnskötare': 'CHILD-MINDER',
  'Bibliotekarie': 'SCHOOL_LIBRARIAN',
  'Lärarassistent': 'TEACHER_ASSISTANT',
  'Fritidspedagog': 'TEACHER_IN_SCHOOL-AGE_EDUCARE',
  'Annan personal': 'OTHER_STAFF',
  'Studie- och yrkesvägledare': 'STUDY_AND_VOCATIONAL_GUIDANCE_COUNSELLOR',
  'Förstelärare': 'SENIOR_TEACHER',
  'Kurator': 'SCHOOL_COUNSELLOR',
  'Skolsköterska': 'SCHOOL_NURSE',
  'Skolläkare': 'SCHOOL_DOCTOR',
  'Skolpsykolog': 'SCHOOL_PSYCHOLOGIST',
  'Speciallärare/specialpedagog': 'SPECIAL_EDUCATION_TEACHER',
  'Skoladministratör': 'SCHOOL_ADMINISTRATOR',
  'Övrig arbetsledning': 'OTHER_MANAGEMENT_FUNCTIONS',
};

const assignmentRoleMap: Record<string, string> = {
  'Mentor': 'MENTOR',
  'Förskollärare': 'PRESCHOOL_TEACHER',
  'Barnskötare': 'CHILD_MINDER',
  'Fritidspedagog': 'LEISURE_TIME_PEDAGOGUE',
  'Specialpedagog': 'SPECIAL_EDUCATOR',
  'Elevhälsopersonal': 'STUDENT_HEALTH_STAFF',
  'Pedagogisk ledare': 'EDUCATIONAL_LEADER',
  'Schemaläggare': 'SCHEDULER',
  'Lärarassistent': 'TEACHER_ASSISTANT',
  'Administrativ personal': 'ADMINISTRATIVE_STAFF',
};

function normalizeDutyRole(value: unknown) {
  if (typeof value != 'string') return undefined;

  return dutyRoleMap[value.trim()];
}

function normalizeAssignmentRole(value: unknown) {
  if (typeof value != 'string') return undefined;

  return assignmentRoleMap[value.trim()];
}

export default (duties: Duty[]): CoreTypes.MixedWithoutInternalId.Teacher[] => {
  if (!Array.isArray(duties))
    throw new Error('(SS12000::From::Duties) Teacher input is not of array type', { cause: 'invalid_input' });

  const toDisplayName = (firstName?: string, lastName?: string) => {
    const first = firstName?.trim();
    const last = lastName?.trim();
    if (first && last) return `${ first } ${ last }`;
    return first || last || undefined;
  };

  return duties
    .map((duty): CoreTypes.MixedWithoutInternalId.Teacher => {
      const person = duty._embedded?.person ?? duty.person;
      const displayName = duty.displayName
        ?? toDisplayName(person?.givenName, person?.familyName)
        ?? person?.displayName;

      return ({
        ids:         duty.id,
        displayName: displayName,
        signature:   duty.signature,
        organizationId: duty.organisation?.id ?? duty.dutyAt?.id,
        dutyRole: normalizeDutyRole(duty.dutyRole),
        description: duty.description,
        dutyPercent: duty.dutyPercent,
        hoursPerYear: duty.hoursPerYear,
        startDate: duty.startDate,
        endDate: duty.endDate,
        assignmentRoles: (duty.assignmentRole ?? duty.assignmentRoles)
          ?.map((assignmentRole) => ({
            groupId: assignmentRole.group?.id,
            assignmentRole: normalizeAssignmentRole(assignmentRole.assignmentRoleType),
            startDate: assignmentRole.startDate,
            endDate: assignmentRole.endDate,
          }))
          .filter((assignmentRole) => Object.values(assignmentRole).some((value) => value !== undefined)),
        ...(duty.person || duty._embedded?.person) && {
          person: {
            type: 'Teacher',
            ...duty.person && { ids: duty.person.id },
            ...duty._embedded?.person && omit(toPerson([ duty._embedded.person ])[0], 'type')
          }
        }
      });
    });
};
